import React, {
  forwardRef,
  ForwardRefRenderFunction,
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo
} from "react";
import {
  Flex,
  FormControl,
  FormLabel,
  Text,
  useColorModeValue,
  FormErrorMessage,
  Box,
  useToast
} from "@chakra-ui/react";
import { FileRejection, useDropzone } from "react-dropzone";
import { FieldError, useFormContext } from "react-hook-form";
import { IconType } from "react-icons";

import { Arquivo } from "./Arquivo";
import { TextGray } from "components/Atomos/Texts/TextGray";
import {
  acceptDropzone,
  descricaoLimites,
  identidadeArquivo,
  intersectarExtensoes,
  mensagensRejeicao,
  QUANTIDADE_MAXIMA,
  TAMANHO_MAXIMO_BYTES,
  validarAnexo
} from "utils/anexoUploadPolicy";

interface Props
  extends Omit<
    React.DetailedHTMLProps<
      React.InputHTMLAttributes<HTMLInputElement>,
      HTMLInputElement
    >,
    "accept"
  > {
  name: string;
  tag?: string;
  label?: string;
  icon?: IconType;
  accept?: string[];
  filesUploading?: string[];
  error?: FieldError;
}

const InputBase: ForwardRefRenderFunction<HTMLInputElement, Props> = (
  {
    name,
    label,
    error,
    icon: IconComponent,
    accept,
    filesUploading = [],
    disabled,
    multiple = true,
    ...props
  },
  ref
) => {
  const containerColor = useColorModeValue("gray.100", "gray.700");
  const borderColor = useColorModeValue("gray.300", "gray.600");
  const { register, unregister, setValue, watch } = useFormContext();
  const files: File[] = watch(name);
  const toast = useToast();

  const extensoes = useMemo(() => intersectarExtensoes(accept), [accept]);

  const onDrop = useCallback(
    (arquivosAceitos: File[], rejeicoes: FileRejection[]) => {
      const selecionados = files ?? [];
      const novos: File[] = [];
      const recusados = mensagensRejeicao(rejeicoes, extensoes);
      const repetidos: string[] = [];

      arquivosAceitos.forEach((arquivo) => {
        // O dropzone só enxerga o lote atual, e o limite de arquivos é do
        // envio inteiro — por isso a contagem soma o que já estava na lista.
        const mensagem = validarAnexo(
          arquivo,
          selecionados.length + novos.length,
          extensoes
        );

        if (mensagem) {
          recusados.push(mensagem);
          return;
        }

        const jaEsta = [...selecionados, ...novos].some(
          (f) => identidadeArquivo(f) === identidadeArquivo(arquivo)
        );

        if (jaEsta) {
          repetidos.push(`"${arquivo.name}" já está na lista.`);
          return;
        }

        novos.push(arquivo);
      });

      // Um mesmo motivo pode valer para vários arquivos do lote; sem o Set o
      // usuário levaria um toast idêntico por arquivo.
      Array.from(new Set(recusados)).forEach((title) =>
        toast({ title, status: "warning" })
      );
      Array.from(new Set(repetidos)).forEach((title) =>
        toast({ title, status: "info" })
      );

      // Recusar um arquivo não pode derrubar os que já estavam selecionados.
      if (novos.length > 0)
        setValue(name, [...selecionados, ...novos], {
          shouldValidate: false
        });
    },
    [setValue, name, files, extensoes, toast]
  );

  const remove = useCallback(
    (fileName: string) => {
      setValue(
        name,
        files.filter((f) => f.name !== fileName),
        {
          shouldValidate: false
        }
      );
    },
    [setValue, name, files]
  );

  const { getRootProps, getInputProps, inputRef } = useDropzone({
    onDrop,
    accept: acceptDropzone(extensoes),
    maxSize: TAMANHO_MAXIMO_BYTES,
    maxFiles: QUANTIDADE_MAXIMA,
    multiple,
    disabled
  });

  // O dropzone precisa do próprio ref para abrir o seletor pelo clique na
  // área; o ref encaminhado pelo formulário não pode tomar o lugar dele.
  const mesclarRefs = useCallback(
    (elemento: HTMLInputElement | null) => {
      (inputRef as MutableRefObject<HTMLInputElement | null>).current =
        elemento;

      if (typeof ref === "function") ref(elemento);
      else if (ref)
        (ref as MutableRefObject<HTMLInputElement | null>).current = elemento;
    },
    [inputRef, ref]
  );

  useEffect(() => {
    register(name);
    return () => {
      unregister(name);
    };
  }, [register, unregister, name]);

  return (
    <FormControl isInvalid={!!error} w="full">
      {!!label && (
        <FormLabel htmlFor={name}>
          <Flex alignItems="center">
            {IconComponent && (
              <TextGray as="span" mr="1.5">
                <IconComponent />
              </TextGray>
            )}
            {label}
          </Flex>
        </FormLabel>
      )}
      <Box>
        <Flex
          bgColor={containerColor}
          borderWidth="2px"
          borderStyle="dashed"
          borderRadius="lg"
          p="4"
          py="6"
          justifyContent="center"
          alignItems="center"
          direction="column"
          {...getRootProps()}
          borderColor={error ? "#E53E3E" : borderColor}
          boxShadow={error ? "0 0 0 1px #e53e3e" : ""}
        >
          <Text fontSize="medium" fontWeight="medium">
            Selecione o(s) arquivo(s)
          </Text>
          <input
            {...getInputProps()}
            {...props}
            id={name}
            disabled={disabled}
            ref={mesclarRefs}
          />

          <TextGray mt="6" textAlign="center" fontSize="sm">
            {descricaoLimites(extensoes)}
          </TextGray>
        </Flex>
      </Box>
      {!!error && (
        <FormErrorMessage mt="0.5" fontSize="xs">
          {error.message as string}
        </FormErrorMessage>
      )}
      {!!files &&
        files.map((x) => (
          <Arquivo
            key={identidadeArquivo(x)}
            file={x}
            onRemove={remove}
            isUploading={filesUploading.filter((f) => f == x.name).length > 0}
          />
        ))}
    </FormControl>
  );
};

export const InputFileAnexo = forwardRef(InputBase);
